import {LoadingOverlay} from '@finpay-development/shared-components';
import {
    Grid,
    Typography,
    Box,
    TableCell,
    TableRow,
    TableContainer,
    TableBody,
    Table,
    Paper,
    TableHead,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {RootState} from '../../../shared/state/root-reducer';
import {AppDispatch} from '../../../shared/state/store';
import {estimateErrorMessage} from '../../../shared/enums';
import {
    Estimate,
    CrossoverEstimateSummary,
    FacilityLevelOfCare,
    PriorCareProvider,
    ThirdPartyPayerStatus,
    QuoteMethod,
    CrossoverSelectionEnum,
    FacilityLevelOfCareWithCrossoverDays
} from '@finpay/estimation-types';
import {formatNumberToUSD} from '../../../shared/calculators';
import {TableColumn} from '../../../shared/model/table-column';
import _ from 'lodash';
import {checkViewOnlyPermissions} from '../../../security/components/access-control';
import {RolePageNames} from '../../../security/model/role-page-names';
import { CrossoverView } from '../../../shared/components/crossover-view';
import PriorCareSummaryTable from '../prior-care-summary-table';
import { useEstimatorContext } from '../../../shared/Hooks/use-estimator-context';
import { ClientFacility } from '../../../shared/model/client-status-card';
import { getPatientDemographics } from '../../../patient/state/patient-thunk';
import { loadSelectedVob } from '../../../shared/state/vob-thunk';

function IOCEstimateDetailsV3() {
    const dispatch = useDispatch<AppDispatch>();
    const params = useParams<{eid?: string}>();
    const estimatorContext = useEstimatorContext();

    const stateFields = {
        isLoadingState: useSelector(
            (state: RootState) => state.adminContext.adminUserContext.isLoading
        ),
        allClientsMap: useSelector(
            (state: RootState) =>
            state.implementationContext.implementationSpecialistClient.allClientsMap
        ),
        userProfile: useSelector(
            (state: RootState) => state.userContext.userProfile
        ),
        isClientLoading: useSelector(
            (state: RootState) => state.implementationContext.implementationSpecialistClient.isLoading
        ),
        selectedPatient: useSelector(
            (state: RootState) => state.patientContext.selectedPatient
        ),
        isLoadingPatient: useSelector(
            (state: RootState) => state.patientContext.isLoading.patientDemographics
        ),
        payersMap: useSelector(
            (state: RootState) => state.adminContext.adminConfigurationContext.payersMap
        ),
        isLoadingPayers: useSelector(
            (state: RootState) => state.adminContext.adminConfigurationContext.isLoading.isLoadingPayer
        ),
        selectedVob: useSelector(
            (state: RootState) => state.vobContext.selectedVob
        ),
        isLoadingVob: useSelector(
            (state: RootState) => state.vobContext.isLoading.selectedVob
        ),
    };

    const {isLoadingState, allClientsMap, userProfile, isClientLoading, selectedPatient, isLoadingPatient, payersMap, isLoadingPayers, selectedVob, isLoadingVob} = stateFields;
    const [selectedFacilityName, setSelectedFacilityName] = useState('');
    const [selectedClientName, setSelectedClientName] = useState('');
    const [selectedClient, setSelectedClient] = useState<any>(null);
    const roleAccess = checkViewOnlyPermissions(
        userProfile.userRole.userRolePages,
        RolePageNames.AdmissionsAdvisor,
        'VOB',
        false
    );
    console.log('roleAccess', roleAccess);
    const estimateId = parseInt(params.eid || '') || 0;
    const paramId = -2;
    const estimate = estimatorContext.selectedEstimate as Estimate | null;
    const isLoadingEstimate = estimatorContext.isLoading.selectedEstimate;

    const locTableHeaders: TableColumn[] = [
        {id: 'colCode', label: 'Code'},
        {id: 'colLocName', label: 'Level of Care'},
        {id: 'colLocDays', label: 'Days/Sessions'},
        {id: 'colLocDays', label: 'Rate per Day'},
        {id: 'colCovered', label: 'Covered'},
    ];

    const estimatedAdjustedCharges =
    formatNumberToUSD(
        estimate?.summary?.coveredCharges! +
            estimate?.summary?.unCoveredCharges!
    )

    // Wait for clients to be loaded before rendering
    const clientsLoaded = allClientsMap && allClientsMap.size > 0;
    
    // Check if payers are loaded
    const payersLoaded = payersMap && payersMap.size > 0;
    
    // Check if patient is loaded or not needed
    const patientLoaded = !estimate?.patientId || (selectedPatient && selectedPatient.patientId > 0);
    
    // Check if VOB is loaded or not needed
    const vobLoaded = !estimate?.vobId || (selectedVob !== null);
    
    // Debug logs
    console.log('Patient loading debug:', {
        estimate: estimate,
        estimatePatientId: estimate?.patientId,
        selectedPatient: selectedPatient,
        patientLoaded: patientLoaded,
        isLoadingPatient: isLoadingPatient
    });
    
    // Load VOB data from estimate's vobId
    useEffect(() => {
        if (estimate && estimate.vobId && (!selectedVob || selectedVob.vobId !== estimate.vobId)) {
            console.log('Dispatching loadSelectedVob with vobId:', estimate.vobId);
            dispatch(loadSelectedVob({ vobId: estimate.vobId }));
        }
    }, [estimate, selectedVob, dispatch]);

    // Load patient data from estimate's patientId
    useEffect(() => {
        console.log('Patient loading useEffect triggered', {
            hasEstimate: !!estimate,
            estimatePatientId: estimate?.patientId,
            selectedPatient: selectedPatient,
            patientMatches: selectedPatient && estimate?.patientId && selectedPatient.patientId === Number(estimate.patientId)
        });
        
        // Only load patient data if patientId exists
        if (estimate && estimate.patientId) {
            // Always dispatch to load patient data when estimate is available
            console.log('Dispatching getPatientDemographics with patientId:', Number(estimate.patientId));
            dispatch(getPatientDemographics(Number(estimate.patientId)));
        }
    }, [estimate, dispatch]);

    useEffect(() => {
        if (clientsLoaded && estimate) {
            const client = allClientsMap.get(Number(estimate.clientId));
            if (client) {
                setSelectedClient(client);
                setSelectedClientName(client.clientName);
                
                // Get facility from client's facilities
                if (client.clientFacilities && client.clientFacilities.length > 0) {
                    const facility = client.clientFacilities.find(
                        (f: ClientFacility) => f.facilityId === Number(estimate?.facilityId)
                    );
                    
                    if (facility) {
                        setSelectedFacilityName(facility.facilityName);
                    }
                }
            }
        }
    }, [allClientsMap, clientsLoaded, estimate]);

    useEffect(() => {
        // fetching estimate based on url param
        const getSavedIOCEstimateName = async () => {
            estimatorContext.loadEstimate({
                paramId: paramId,
                estimationId: estimateId,
            });
        };
        getSavedIOCEstimateName();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function LocTable(props: {levelsOfCare: FacilityLevelOfCareWithCrossoverDays[]}) {
        const {levelsOfCare} = props;

        return (
            <React.Fragment>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {locTableHeaders.map(column => (
                                    <TableCell key={column.id}>
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {_.sortBy(levelsOfCare, 'sortOrder')
                                .filter((loc: FacilityLevelOfCareWithCrossoverDays) => {
                                    if (loc.locType === 1) return false;
                                    return true;
                                })
                                .map(
                                    (
                                        row: FacilityLevelOfCareWithCrossoverDays,
                                        index: number
                                    ) => {
                                        const days = estimate?.isPlanYearCrossover
                                            ? estimate?.crossoverSelection === CrossoverSelectionEnum.BEFORE && row?.losDaysAfterCrossover
                                                ? row?.losDaysBeforeCrossover
                                                : row?.losDays
                                            : row?.losDays;
                                        return (
                                            <TableRow key={index}>
                                                <TableCell test-id={`loc-code-${index}`}>
                                                    {
                                                        row?.facilityLevelOfCareCode
                                                    }
                                                </TableCell>
                                                <TableCell test-id={`loc-name-${index}`}>
                                                    {
                                                        row?.facilityLevelOfCareName
                                                    }
                                                    <div
                                                        style={{
                                                            fontSize: '8pt',
                                                        }}
                                                    >
                                                        {selectedFacilityName}
                                                    </div>
                                                </TableCell>
                                                <TableCell test-id={`loc-days-${index}`}>
                                                    {days}
                                                </TableCell>
                                                <TableCell test-id={`loc-rate-${index}`}>
                                                    {row?.pdrRate}
                                                </TableCell>
                                                <TableCell test-id={`loc-covered-${index}`}>
                                                    {row?.isCovered ? (
                                                        <>Yes</>
                                                    ) : (
                                                        <>No</>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }
                                )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </React.Fragment>
        );
    }

    function PriorCare() {
        const priorLevelsOfCareSummary = estimate?.priorCareSummary;
        const genericPriorLoc = estimate?.priorCare;
        // If priorCare doesn't exist, return null to hide this section
        if (!genericPriorLoc) {
            return null;
        }

        function estimatedPriorLos() {
            let los = 0;
            genericPriorLoc?.loc.forEach((loc: FacilityLevelOfCare) => {
                los += loc?.losDays;
            });
            return los;
        }

        return (
            <>
                <Typography variant="subtitle2" className="mt-4" test-id="prior-care-title">
                    Estimated Prior Care Adjustment Details
                </Typography>
                <Grid container direction="row">
                    <Grid item direction="column" md={5}>
                        <Typography variant="body2">
                            Prior Care Class
                        </Typography>
                        {genericPriorLoc?.priorCareProvider ===
                        PriorCareProvider.THIRDPARTY
                            ? 'Third Party'
                            : 'Client'}
                    </Grid>
                    <Grid item direction="column" md={5}>
                        <Typography variant="body2">
                            {genericPriorLoc?.priorCareProvider ===
                            PriorCareProvider.THIRDPARTY
                                ? 'Third Party Payer Status'
                                : 'Facility'}
                        </Typography>
                        {genericPriorLoc?.priorCareProvider ===
                        PriorCareProvider.THIRDPARTY
                            ? genericPriorLoc?.thirdPartyPayerStatus ===
                              ThirdPartyPayerStatus.INNETW
                                ? 'Average In-Network'
                                : 'Average Out-of-Network'
                            : selectedFacilityName}
                    </Grid>
                </Grid>
                <LocTable levelsOfCare={genericPriorLoc?.loc!.map((loc: FacilityLevelOfCare) => ({
                    ...loc,
                })) as FacilityLevelOfCareWithCrossoverDays[]} />
                {estimate.priorCareSummary && <PriorCareSummaryTable priorCareSummary={estimate.priorCareSummary} />}
            </>
        );
    }

    function NormalPfr(props: {hasCrossover: boolean}) {
        const {hasCrossover} = props;
        const financialSummary: any = estimate?.summary;

        return (
            <>
                <Typography variant="subtitle2" className="mt-2" test-id="pfr-details-title">
                    PFR Details {hasCrossover && <span test-id="crossover-indicator"> (with Crossover)</span>}
                </Typography>
                <TableContainer component={Paper}>
                    <Table size="small" className="alternating-row">
                        <TableBody>
                            <TableRow className="py-3">
                                <TableCell test-id="estimated-copay">Estimated Co-Pay</TableCell>
                                <TableCell align="right">
                                    {formatNumberToUSD(
                                        financialSummary.patientTotalCoPays
                                    )}
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell test-id="estimated-deductible">Estimated Deductible</TableCell>
                                <TableCell align="right">
                                    {formatNumberToUSD(
                                        financialSummary.deductibleSpent
                                    )}
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell test-id="estimated-coinsurance">Estimated Co-Insurance</TableCell>
                                <TableCell align="right">
                                    {formatNumberToUSD(
                                        financialSummary.patientTotalCoInsurance
                                    )}
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell></TableCell>
                                <TableCell
                                    align="right"
                                    sx={{borderTop: '2px solid #999'}}
                                >
                                    <strong>
                                        {formatNumberToUSD(
                                            financialSummary.coveredSvcsPfr
                                        )}
                                    </strong>
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell test-id="oop-max-remaining">
                                    Out of Pocket Max Remaining
                                </TableCell>
                                <TableCell align="right">
                                    {financialSummary.remainingOopMaxFromVob !==
                                    -1 ? (
                                        formatNumberToUSD(
                                            financialSummary.remainingOopMaxFromVob
                                        )
                                    ) : (
                                        <>N/A</>
                                    )}
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell test-id="pfr-covered-services">PFR for Covered Services</TableCell>
                                <TableCell align="right">
                                    {formatNumberToUSD(financialSummary.netPFR)}
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell test-id="uncovered-services">Uncovered Services</TableCell>
                                <TableCell align="right">
                                    {formatNumberToUSD(
                                        financialSummary.unCoveredCharges
                                    )}
                                </TableCell>
                            </TableRow>
                            {financialSummary.totalSurcharges > 0 && (
                                <>
                                    <TableRow className="py-3">
                                        <TableCell test-id="pfr-subtotal">PFR Subtotal</TableCell>
                                        <TableCell align="right">
                                            {formatNumberToUSD(
                                                financialSummary.subtotalPFR
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="py-3">
                                        <TableCell test-id="admission-assessment">
                                            Admission Assessment
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatNumberToUSD(
                                                financialSummary.totalSurcharges
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </>
                            )}
                            <TableRow className="py-3">
                                <TableCell test-id="estimated-pfr-label">
                                    <Typography variant="subtitle2">
                                        Estimated Patient Financial
                                        Responsibility
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" test-id="estimated-pfr-value">
                                    <Typography variant="subtitle2">
                                        {formatNumberToUSD(
                                            financialSummary.totalPFR
                                        )}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        );
    }

    function FinAssistPFR() {
        // calculate PFR for plan crossover
        const crossoverPolicies: CrossoverEstimateSummary =
            estimate?.crossOverSummary!;

        const financialSummary = estimate?.summary!;

        const showScholarshipPct = () => {
            let scholarship = 0;
            if (estimate?.financialAssistance?.scholarshipPercentage!) {
                scholarship =
                    estimate?.financialAssistance?.scholarshipPercentage * 100;
            }
            return scholarship.toFixed(0) + '%';
        };

        const showScholarshipAmt = (pfr: number) => {
            let scholarshipPct = 1;
            if (estimate?.financialAssistance?.scholarshipPercentage) {
                scholarshipPct =
                    estimate?.financialAssistance?.scholarshipPercentage;
            }
            return formatNumberToUSD(pfr * scholarshipPct);
        };

        const pfrAdjWithCrossover = () => {
            const scholarshipAmt =
                (crossoverPolicies?.summaryBeforeCrossover?.totalPFR! +
                    crossoverPolicies?.summaryWithCrossover?.totalPFR!) *
                estimate?.financialAssistance?.scholarshipPercentage!;
            return formatNumberToUSD(
                crossoverPolicies?.summaryBeforeCrossover?.totalPFR! +
                    crossoverPolicies?.summaryWithCrossover?.totalPFR! -
                    scholarshipAmt
            );
        };

        const pfrAdjustment = () => {
            const scholarshipAmt =
                financialSummary.totalPFR *
                estimate?.financialAssistance?.scholarshipPercentage!;
            return formatNumberToUSD(
                financialSummary.totalPFR - scholarshipAmt
            );
        };

        return (
            <>
                <Typography variant="subtitle2" className="mt-4" test-id="financial-assistance-title">
                    Estimated PFR After Financial Assistance
                </Typography>

                <TableContainer component={Paper}>
                    <Table size="small" className="alternating-row">
                        <TableBody>
                            {estimate?.isPlanYearCrossover ? (
                                <>
                                    <TableRow className="py-3">
                                        <TableCell test-id="crossover-year-pfr">
                                            Crossover Year PFR
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatNumberToUSD(
                                                crossoverPolicies
                                                    ?.summaryWithCrossover
                                                    ?.totalPFR
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="py-3">
                                        <TableCell test-id="current-year-pfr">Current Year PFR</TableCell>
                                        <TableCell align="right">
                                            {formatNumberToUSD(
                                                crossoverPolicies
                                                    ?.summaryBeforeCrossover
                                                    ?.totalPFR
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="py-3">
                                        <TableCell test-id="crossover-pfr-subtotal">PFR Subtotal</TableCell>
                                        <TableCell align="right">
                                            {formatNumberToUSD(
                                                crossoverPolicies
                                                    ?.summaryWithCrossover
                                                    ?.totalPFR! +
                                                    crossoverPolicies
                                                        ?.summaryBeforeCrossover
                                                        ?.totalPFR!
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="py-3">
                                        <TableCell>
                                            Financial Assistance Scholarship (
                                            <span test-id="scholarship-percentage">{showScholarshipPct()}</span>)
                                        </TableCell>
                                        <TableCell align="right" test-id="scholarship-amount">
                                            {showScholarshipAmt(
                                                crossoverPolicies
                                                    ?.summaryWithCrossover
                                                    ?.totalPFR! +
                                                    crossoverPolicies
                                                        ?.summaryBeforeCrossover
                                                        ?.totalPFR!
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="py-3">
                                        <TableCell test-id="pfr-after-assistance-label">
                                            <Typography variant="subtitle2" test-id="pfr-after-assistance-label">
                                                PFR After Financial Assistance
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" test-id="pfr-after-assistance-value">
                                            <Typography variant="subtitle2" test-id="pfr-after-assistance-value">
                                                {pfrAdjWithCrossover()}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </>
                            ) : (
                                <>
                                    <TableRow className="py-3">
                                        <TableCell test-id="total-patient-pfr">Total Patient PFR</TableCell>
                                        <TableCell align="right">
                                            {formatNumberToUSD(
                                                financialSummary.totalPFR
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="py-3">
                                        <TableCell>
                                            Financial Assistance Scholarship (
                                            <span test-id="scholarship-percentage">{showScholarshipPct()}</span>)
                                        </TableCell>
                                        <TableCell align="right" test-id="scholarship-amount">
                                            {showScholarshipAmt(
                                                financialSummary.totalPFR
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="py-3">
                                        <TableCell test-id="pfr-after-assistance">
                                            PFR After Financial Assistance
                                        </TableCell>
                                        <TableCell align="right" test-id="pfr-after-assistance">
                                            {pfrAdjustment()}
                                        </TableCell>
                                    </TableRow>
                                    {financialSummary.scholarshipAdjustedSurcharge >
                                        0 && (
                                        <TableRow className="py-3">
                                            <TableCell test-id="scholarship-adjusted-surcharge">
                                                Admission Assessment
                                            </TableCell>
                                            <TableCell align="right" test-id="scholarship-adjusted-surcharge">
                                                {formatNumberToUSD(
                                                    financialSummary.scholarshipAdjustedSurcharge
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow className="py-3">
                                        <TableCell test-id="total-pfr-label">
                                            <Typography variant="subtitle2" test-id="total-pfr-label">
                                                Total PFR
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" test-id="total-pfr-value">
                                            <Typography variant="subtitle2" test-id="total-pfr-value">
                                                {pfrAdjustment()}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        );
    }

    // Helper function to map QuoteMethod enum to display name
    function getQuoteMethodName(quoteMethod?: QuoteMethod): string {
        switch (quoteMethod) {
            case QuoteMethod.AVGLOS:
                return 'Average Length of Stay';
            case QuoteMethod.SCA:
                return 'Single Case Agreement';
            case QuoteMethod.RLOS:
                return 'Recommended Length of Stay';
            case QuoteMethod.MANUAL:
                return 'Manual';
            default:
                return 'Unknown';
        }
    }
    
    // Helper function to format phone number as (XXX) XXX-XXXX
    function formatPhoneNumber(phoneNumber?: string): string {
        if (!phoneNumber) return 'N/A';
        
        // Remove all non-digit characters
        const cleaned = phoneNumber.replace(/\D/g, '');
        
        // Check if the input is of correct length
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        
        if (match) {
            return '(' + match[1] + ') ' + match[2] + '-' + match[3];
        }
        
        // If input doesn't match the expected format, return as is
        return phoneNumber;
    }
    
    // Helper function to get payer name from payerId
    function getPayerName(payerId?: number): string {
        if (!payerId || !payersMap || !payersLoaded) return 'Unknown';
        
        const payer = payersMap.get(payerId);
        return payer ? payer.payorName : 'Unknown';
    }

    return (
        <Grid>
            {roleAccess && clientsLoaded && patientLoaded && vobLoaded && (
                <main className="patient-details">
                    <Box className="content extra-padding">
                        <Grid
                            sx={{
                                marginTop: '2rem',
                                marginBottom: '2rem',
                                display: 'flex',
                            }}
                        >
                            <Grid item xs={10}>
                                <Typography
                                    variant="h3"
                                    className="pt-0_5"
                                    style={{
                                        display: 'flex',
                                        alignContent: 'center',
                                        paddingLeft: '6px',
                                        fontSize: '2rem',
                                    }}
                                    test-id="estimate-details-title"
                                >
                                    Estimate Details
                                </Typography>
                            </Grid>
                        </Grid>
                        <Box
                            border={1}
                            borderColor="primary.main"
                            borderRadius="5px"
                            className="mb-5"
                            sx={{
                                marginTop: '1rem',
                                marginBottom: '4rem !important',
                            }}
                            test-id="estimate-details-container"
                        >
                            <Box style={{margin: '0.5em', padding: '12px'}}>
                                {estimate ? (
                                    <>
                                        {/* Client and Facility information */}
                                        <Grid container direction="row" spacing={2}>
                                            <Grid item direction="column" md={6}>
                                                <Typography variant="body2">
                                                    Client
                                                </Typography>
                                                <span test-id="client-info">{`${selectedClientName} - ${estimate?.clientId}`}</span>
                                            </Grid>
                                            <Grid item direction="column" md={6}>
                                                <Typography variant="body2">
                                                    Facility
                                                </Typography>
                                                <span test-id="facility-info">{`${selectedFacilityName} - ${estimate?.facilityId}`}</span>
                                            </Grid>
                                            
                                            {/* Patient Information - only show if patientId exists */}
                                            {estimate.patientId && (
                                                <>
                                                    <Grid item xs={12}>
                                                        {!patientLoaded && !isLoadingPatient && estimate?.patientId && (
                                                            <Typography variant="body2" color="error">
                                                                Unable to load patient data. Patient ID: {estimate.patientId}
                                                            </Typography>
                                                        )}
                                                    </Grid>
                                                    <Grid item direction="column" md={3}>
                                                        <Typography variant="body2">
                                                            Patient Name
                                                        </Typography>
                                                        <span test-id="patient-name">
                                                            {selectedPatient ? 
                                                                `${selectedPatient.contact?.title === '-1' ? '' : selectedPatient.contact?.title || ''} ${selectedPatient.contact?.firstName || ''} ${selectedPatient.contact?.middleName || ''} ${selectedPatient.contact?.lastName || ''}`.replace(/\s+/g, ' ').trim() 
                                                                : 'Loading...'}
                                                        </span>
                                                    </Grid>
                                                    <Grid item direction="column" md={3}>
                                                        <Typography variant="body2">
                                                            Preferred Name
                                                        </Typography>
                                                        <span test-id="patient-preferred-name">
                                                            {selectedPatient && selectedPatient.contact?.preferredName ? selectedPatient.contact.preferredName : 'N/A'}
                                                        </span>
                                                    </Grid>
                                                    <Grid item direction="column" md={3}>
                                                        <Typography variant="body2">
                                                            Date of Birth
                                                        </Typography>
                                                        <span test-id="patient-dob">
                                                            {selectedPatient && selectedPatient.contact?.birthDate ? 
                                                                new Date(selectedPatient.contact.birthDate).toLocaleDateString('en-US', {timeZone: 'UTC'}) 
                                                                : 'N/A'}
                                                        </span>
                                                    </Grid>
                                                    <Grid item direction="column" md={3}>
                                                        <Typography variant="body2">
                                                            Patient ID
                                                        </Typography>
                                                        <span test-id="patient-id">
                                                            {selectedPatient ? selectedPatient.patientId : 'Loading...'}
                                                        </span>
                                                    </Grid>
                                                    
                                                    {/* Second row of patient information */}
                                                    <Grid item direction="column" md={3}>
                                                        <Typography variant="body2">
                                                            Primary Phone
                                                        </Typography>
                                                        <span test-id="patient-phone">
                                                            {selectedPatient && selectedPatient.contact?.primPhoneNum ? formatPhoneNumber(selectedPatient.contact.primPhoneNum) : 'N/A'}
                                                        </span>
                                                    </Grid>
                                                    <Grid item direction="column" md={3}>
                                                        <Typography variant="body2">
                                                            Email Address
                                                        </Typography>
                                                        <span test-id="patient-email">
                                                            {selectedPatient && selectedPatient.contact?.email ? selectedPatient.contact.email : 'N/A'}
                                                        </span>
                                                    </Grid>
                                                    <Grid item direction="column" md={6}>
                                                    </Grid>
                                                </>
                                            )}
                                            
                                            {/* VOB Information - only show if vobId exists and VOB is loaded */}
                                            {estimate?.vobId && selectedVob && (
                                                <>
                                                    <Grid item direction="column" md={3}>
                                                        <Typography variant="body2">
                                                            Primary Payer
                                                        </Typography>
                                                        <span test-id="vob-payer">
                                                            {selectedVob?.payor?.payorId ? getPayerName(Number(selectedVob.payor.payorId)) : 'N/A'}
                                                        </span>
                                                    </Grid>
                                                    <Grid item direction="column" md={3}>
                                                        <Typography variant="body2">
                                                            Member ID
                                                        </Typography>
                                                        <span test-id="vob-member-id">
                                                            {selectedVob?.policyNum || 'N/A'}
                                                        </span>
                                                    </Grid>
                                                    <Grid item direction="column" md={3}>
                                                        <Typography variant="body2">
                                                            VOB Create Date
                                                        </Typography>
                                                        <span test-id="vob-create-date">
                                                            {selectedVob?.createDt ? new Date(selectedVob.createDt).toLocaleDateString('en-US') : 'N/A'}
                                                        </span>
                                                    </Grid>
                                                    <Grid item direction="column" md={3}>
                                                        <Typography variant="body2">
                                                            VOB ID
                                                        </Typography>
                                                        <span test-id="vob-id">
                                                            {selectedVob.vobId}
                                                        </span>
                                                    </Grid>
                                                    
                                                    <Grid item direction="column" md={6}>
                                                        <Typography variant="body2">
                                                            Policy Begin Date
                                                        </Typography>
                                                        <span test-id="vob-policy-begin-date">
                                                            {selectedVob?.policyBeginDate ? new Date(selectedVob.policyBeginDate).toLocaleDateString('en-US') : 'N/A'}
                                                        </span>
                                                    </Grid>
                                                    <Grid item direction="column" md={6}>
                                                        <Typography variant="body2">
                                                            Policy End Date
                                                        </Typography>
                                                        <span test-id="vob-policy-end-date">
                                                            {selectedVob?.policyEndDate ? new Date(selectedVob.policyEndDate).toLocaleDateString('en-US') : 'N/A'}
                                                        </span>
                                                    </Grid>
                                                </>
                                            )}
                                            
                                            <Grid item direction="column" md={6}>
                                                <Typography variant="body2">
                                                    Quote Method
                                                </Typography>
                                                <span test-id="estimate-quote-method">
                                                    {getQuoteMethodName(estimate?.quoteMethod)}
                                                </span>
                                            </Grid>
                                            <Grid item direction="column" md={12}>
                                                <Typography variant="body2">
                                                    Anticipated Admission Date
                                                </Typography>
                                                <span test-id="estimate-admission-date">
                                                    {new Date(
                                                        estimate?.anticipatedAdmitDate || new Date()
                                                    ).toLocaleDateString('en-US')}
                                                </span>
                                            </Grid>
                                        </Grid>

                                        {/* Prior Care section */}
                                        {estimate.priorCare && <PriorCare />}

                                        {/* Care Plan Details section */}
                                        {estimate.facilityLevelOfCare?.length! > 0 && (
                                            <>
                                                <Typography variant="subtitle2" className="mt-4" test-id="care-plan-title">
                                                    Care Plan Details
                                                </Typography>
                                                <LocTable levelsOfCare={estimate.facilityLevelOfCare!.map((loc: FacilityLevelOfCare) => ({
                                                    ...loc,
                                                })) as FacilityLevelOfCareWithCrossoverDays[]} />
                                                <TableContainer component={Paper}>
                                                    <Table size="small" className="alternating-row">
                                                        <TableBody>
                                                            <TableRow className="py-3 light-green-row">
                                                                <TableCell test-id="estimated-adjusted-charges">
                                                                    Estimated Adjusted Charges
                                                                </TableCell>
                                                                <TableCell align="right" test-id="estimated-adjusted-charges">
                                                                    {estimatedAdjustedCharges}
                                                                </TableCell>
                                                            </TableRow>
                                                            <TableRow className="py-3 light-green-row">
                                                                <TableCell test-id="estimated-length-of-stay">
                                                                    Estimated Length of Stay
                                                                </TableCell>
                                                                <TableCell align="right" test-id="estimated-length-of-stay">
                                                                    {estimate?.summary?.lengthOfStay}
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </>
                                        )}
                                        {!estimate.isPlanYearCrossover && estimate?.summary && (
                                            <NormalPfr hasCrossover={false} />
                                        )}
                                        {estimate.crossOverSummary && estimate.isPlanYearCrossover && (
                                            <CrossoverView estimate={estimate} isPriorCareSummary={false} />
                                        )}
                                        {estimate.financialAssistance && <FinAssistPFR />}
                                    </>
                                ) : (
                                    <Typography>
                                        {estimateErrorMessage.loadingError}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </main>
            )}
            {!roleAccess && !isLoadingState && (
                <Typography className="access-error" test-id="access-error">
                    <span className="message">
                        <LockIcon
                            style={{paddingRight: '8px', fontSize: '1.6em'}}
                        />
                        {estimateErrorMessage.authenticationError}
                    </span>
                </Typography>
            )}
            {isLoadingEstimate || isLoadingState || isClientLoading || !clientsLoaded || !patientLoaded || isLoadingPayers || isLoadingVob ? <LoadingOverlay /> : null}
            {!isLoadingEstimate && estimate && estimate.patientId && !patientLoaded && (
                <Box p={3} textAlign="center" test-id="loading-patient-info">
                    <Typography variant="h6">
                        Loading patient information...
                    </Typography>
                    <Typography variant="body1">
                        Retrieving patient data for ID: {estimate.patientId}
                    </Typography>
                </Box>
            )}
        </Grid>
    );
}
export default IOCEstimateDetailsV3; 